import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { DollarSign, Download, RefreshCw, AlertTriangle, FileText } from 'lucide-react';

export default function Payroll() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isHR = user?.role === 'hr' || user?.role === 'manager' || isAdmin;
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => { load(); }, [month, year]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/payroll?month=${month}&year=${year}`);
      setPayrolls(res.data.payrolls);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/api/payroll/generate', { month, year });
      alert(res.data.message);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Error generating payroll'); }
    finally { setGenerating(false); }
  };

  const downloadPdf = async (payrollId) => {
    try {
      const res = await api.get(`/api/payroll/${payrollId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `salary_slip_${payrollId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { alert('Error downloading PDF'); }
  };

  const totalPay = payrolls.reduce((s, p) => s + (p.total_pay || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div><h3 className="text-xl font-bold text-white">Payroll</h3><p className="text-dark-400 text-sm">Monthly salary and commission management</p></div>
        <div className="flex items-center gap-2">
          <select value={month} onChange={e => setMonth(parseInt(e.target.value))} className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
            {Array.from({length: 12}, (_, i) => <option key={i+1} value={i+1}>{new Date(2000, i).toLocaleString('default', {month: 'long'})}</option>)}
          </select>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm">
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {isAdmin && <button onClick={generate} disabled={generating} className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-dark-900 font-medium rounded-lg text-sm disabled:opacity-50"><RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />{generating ? 'Generating...' : 'Generate'}</button>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="glass rounded-xl p-4"><div className="text-2xl font-bold text-white">{payrolls.length}</div><div className="text-sm text-dark-400">Payslips</div></div>
        <div className="glass rounded-xl p-4"><div className="text-2xl font-bold text-green-400">Rs. {totalPay.toLocaleString()}</div><div className="text-sm text-dark-400">Total Payout</div></div>
        <div className="glass rounded-xl p-4"><div className="text-2xl font-bold text-yellow-400">{payrolls.filter(p => p.consecutive_miss_flag).length}</div><div className="text-sm text-dark-400">Flagged Executives</div></div>
      </div>

      {loading ? <div className="flex justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div></div> :
      payrolls.length === 0 ? <div className="glass rounded-xl p-8 text-center"><DollarSign className="w-12 h-12 text-dark-500 mx-auto mb-3" /><p className="text-dark-400">No payroll data for this period</p>{isAdmin && <p className="text-dark-500 text-xs mt-1">Click Generate to create payroll</p>}</div> :
      <div className="glass rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-700">
                <th className="text-left py-3 px-4 text-dark-400 font-medium">Executive</th>
                <th className="text-right py-3 px-4 text-dark-400 font-medium">Meetings</th>
                <th className="text-right py-3 px-4 text-dark-400 font-medium">Meeting Pay</th>
                <th className="text-right py-3 px-4 text-dark-400 font-medium">Bonus</th>
                <th className="text-right py-3 px-4 text-dark-400 font-medium">Commission</th>
                <th className="text-right py-3 px-4 text-dark-400 font-medium">Total</th>
                <th className="text-right py-3 px-4 text-dark-400 font-medium">Attendance</th>
                <th className="text-center py-3 px-4 text-dark-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payrolls.map(p => (
                <tr key={p.id} className={`border-b border-dark-800 hover:bg-dark-800/30 ${p.consecutive_miss_flag ? 'bg-red-500/5' : ''}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className="text-white">{p.agent_name}</span>
                      {p.consecutive_miss_flag && <AlertTriangle className="w-4 h-4 text-red-400" title="Missed target 2 months" />}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right text-white">{p.completed_meetings}/{p.target_meetings}</td>
                  <td className="py-3 px-4 text-right text-white">Rs. {p.meeting_payout?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-green-400">Rs. {p.bonus_earned?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-blue-400">Rs. {p.total_commission?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-white font-medium">Rs. {p.total_pay?.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right text-dark-300">{p.attendance_days}d</td>
                  <td className="py-3 px-4 text-center">
                    <button onClick={() => downloadPdf(p.id)} className="p-1.5 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 rounded" title="Download Salary Slip">
                      <Download className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>}
    </div>
  );
}
